import React, { PropsWithChildren, useEffect, useState } from 'react';
import { TextInput } from '../TextInput';
import { FilterEventHandler, IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { useSortEventListenerFix } from './useSortEventListenerFix';
import styled from 'styled-components';
import { convertNullToUndefined } from '../../utilities/convertNullToUndefined';
import { useDelayedOnFilterEffect } from './useDelayedOnFilterEffect';
import { ICustomTextFilterProps } from 'react-bootstrap-table2-filter';
import { IconType } from '../Icon';

export interface ITextFilterProps<T extends object = {}> extends Omit<ICustomTextFilterProps, 'type'> {
  onFilter: FilterEventHandler;
  column: IBootstrapTableColumnProps<T>;
}

const StyledTextInput = styled(TextInput)`
  width: 100%;
`;

export const TextFilter = <T extends object = {}>({
  defaultValue,
  delay,
  onFilter,
}: PropsWithChildren<ITextFilterProps<T>>) => {
  //FYI: preventing event bubbling because BootstrapTable sorts when focusing filter elements
  const clickEventInputRef = useSortEventListenerFix();
  const [value, setValue] = useState<string | undefined>(defaultValue);

  useEffect(() => setValue(defaultValue), [defaultValue]);

  const isDelaying = useDelayedOnFilterEffect(value, delay, onFilter);

  return (
    <StyledTextInput
      appendIconType={!!delay && isDelaying ? IconType.Loading : undefined}
      getInputRef={clickEventInputRef}
      onChange={(newValue) => setValue(convertNullToUndefined(newValue))}
      value={value}
    />
  );
};

export interface ITextFilterRendererProps extends Omit<ICustomTextFilterProps, 'type'> {}

export const TextFilterRenderer =
  <T extends object>(config: ITextFilterRendererProps) =>
  (onFilter: FilterEventHandler, column: IBootstrapTableColumnProps<T>) => (
    <TextFilter {...config} column={column} onFilter={onFilter} />
  );
